import { useCallback, useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import { useLocation, useNavigate } from "react-router-dom";
import Alert from "@mui/material/Alert";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import CircularProgress from "@mui/material/CircularProgress";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import FormControl from "@mui/material/FormControl";
import Grid from "@mui/material/Grid";
import Icon from "@mui/material/Icon";
import IconButton from "@mui/material/IconButton";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import Tab from "@mui/material/Tab";
import Tabs from "@mui/material/Tabs";
import TextField from "@mui/material/TextField";
import Tooltip from "@mui/material/Tooltip";

import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import { useAdminAuth } from "auth/AdminAuthProvider";
import {
  adminRequest,
  createResource,
  listAllResource,
  resourceAction,
  unwrapItems,
  updateResource,
  uploadMedia,
} from "services/adminApi";

const configs = {
  destinations: {
    title: "الدول والمدن",
    description: "أنشئ أو حدّث المحتوى الذي يظهر للمسافر، ثم راجعه قبل النشر.",
    createLabel: "إضافة دولة أو مدينة",
    searchLabel: "ابحث باسم الدولة أو المدينة",
    id: "destinationId",
    primary: (item) => item.displayName || item.country || "وجهة جديدة",
    secondary: (item) => item.area || "",
  },
  places: {
    title: "الأماكن والأنشطة",
    description: "المعالم والأنشطة والمطاعم المرتبطة بالوجهات.",
    createLabel: "إضافة مكان أو نشاط",
    searchLabel: "ابحث باسم المكان أو الوجهة",
    id: "placeId",
    primary: (item) => item.nameAr || "مكان جديد",
    secondary: (item) => item.placeType || "",
  },
  notices: {
    title: "التنبيهات",
    description: "تنبيهات عامة أو موجهة لدولة أو وجهة ضمن فترة زمنية.",
    createLabel: "إنشاء تنبيه",
    searchLabel: "ابحث بعنوان التنبيه أو الدولة",
    id: "noticeId",
    primary: (item) => item.title || "تنبيه جديد",
    secondary: (item) => item.scope || "",
  },
  users: {
    title: "المستخدمون",
    description: "عرض المستخدمين وتعطيل أو إعادة تفعيل الحسابات عند الحاجة.",
    searchLabel: "ابحث بالاسم أو البريد الإلكتروني",
    id: "userId",
    primary: (item) => item.displayName || item.email || item.userId,
    secondary: (item) => item.email || "",
    readOnly: true,
  },
  trips: {
    title: "الرحلات والدعم",
    description: "عرض الرحلات وإضافة ملاحظات دعم داخلية دون تغيير بيانات المسافر.",
    searchLabel: "ابحث بعنوان الرحلة أو حالتها",
    id: "tripId",
    primary: (item) => item.title || "رحلة",
    secondary: (item) => item.status || "",
    readOnly: true,
  },
  audit: {
    title: "سجل التدقيق",
    description: "سجل كامل لعمليات التحرير والنشر والأرشفة وإدارة الحسابات.",
    searchLabel: "ابحث بالعملية أو المستخدم",
    id: "auditId",
    primary: (item) => item.action,
    secondary: (item) => `${item.entityType || ""} · ${item.actor?.name || ""}`,
    readOnly: true,
  },
  "admin-users": {
    title: "فريق الإدارة",
    description: "الأدوار والصلاحيات المطبقة داخل لوحة الإدارة.",
    searchLabel: "ابحث بالاسم أو البريد الإلكتروني",
    id: "group",
    primary: (item) => item.group,
    secondary: (item) => (item.permissions || []).join("، "),
    readOnly: true,
  },
  "monthly-events": {
    title: "يا مسافر Moment",
    description: "جلب مباشر من API Ticketmaster، ترجمة عربية، ثم مراجعة ونشر في التطبيق.",
    createLabel: "إضافة حدث",
    searchLabel: "ابحث باسم الحدث أو الدولة",
    id: "eventId",
    primary: (item) => item.titleAr || "حدث جديد",
    secondary: (item) =>
      [item.cityNameAr, item.countryNameAr, item.startsAt].filter(Boolean).join(" · "),
    automation: {
      path: "/admin/monthly-events/automation/fetch",
      confirmation:
        "سيتم جلب أحداث جديدة من Ticketmaster وترجمتها إلى العربية وحفظها كمسودات للمراجعة. متابعة؟",
    },
  },
};

const fieldGroups = {
  destinations: [
    {
      label: "الهوية",
      fields: [
        ["country", "الدولة"],
        ["area", "المدينة أو المنطقة"],
        ["displayName", "الاسم الظاهر"],
        ["category", "التصنيف"],
        ["travelStyles", "أنماط السفر، سطر لكل نمط", "lines"],
        ["latitude", "خط العرض"],
        ["longitude", "خط الطول"],
        ["tagline", "الوصف المختصر", "multiline"],
        ["heroImage", "صورة الغلاف"],
      ],
    },
    {
      label: "معلومات السفر",
      fields: [
        ["currency", "العملة"],
        ["language", "اللغة"],
        ["transportSummary", "ملخص التنقل", "multiline"],
        ["bestTime", "أفضل وقت للزيارة، سطر لكل عنصر", "lines"],
        ["attractions", "أبرز الأماكن، سطر لكل عنصر", "lines"],
      ],
    },
    {
      label: "العائلة والميزانية",
      fields: [
        ["familyRating", "تقييم العائلات"],
        ["familyReasons", "سبب الملاءمة للعائلات", "multiline"],
        ["ageTags", "وسوم الأعمار، سطر لكل وسم", "lines"],
        ["adminNotice", "ملاحظة المسافر", "multiline"],
      ],
    },
    {
      label: "النصائح والدليل",
      fields: [
        ["transportTips", "نصائح التنقل، سطر لكل عنصر", "lines"],
        ["safetyCultureTips", "السلامة والثقافة، سطر لكل عنصر", "lines"],
        ["travelerTips", "نصائح المسافر، سطر لكل عنصر", "lines"],
        ["countryGuide.title", "عنوان دليل الدولة"],
        ["countryGuide.introduction", "مقدمة الدليل", "multiline"],
        ["countryGuide.entryDisclaimer", "تنبيه متطلبات الدخول", "multiline"],
      ],
    },
  ],
  places: [
    {
      label: "بيانات المكان",
      fields: [
        ["destinationId", "معرّف الوجهة"],
        ["nameAr", "الاسم بالعربية"],
        ["nameEn", "الاسم بالإنجليزية"],
        ["placeType", "نوع المكان"],
        ["descriptionAr", "الوصف", "multiline"],
        ["mapUrl", "رابط الخريطة"],
        ["ageTags", "وسوم الأعمار، سطر لكل وسم", "lines"],
      ],
    },
  ],
  notices: [
    {
      label: "بيانات التنبيه",
      fields: [
        ["title", "العنوان"],
        ["message", "الرسالة", "multiline"],
        ["scope", "النطاق"],
        ["country", "الدولة"],
        ["destinationId", "معرّف الوجهة"],
        ["priority", "الأولوية"],
        ["startAt", "بداية الظهور"],
        ["endAt", "نهاية الظهور"],
      ],
    },
  ],
  "monthly-events": [
    {
      label: "محتوى الحدث",
      fields: [
        ["titleAr", "اسم الحدث بالعربية"],
        ["titleEn", "اسم الحدث الأصلي"],
        ["summaryAr", "الملخص العربي", "multiline"],
        ["descriptionAr", "الوصف العربي", "multiline"],
        ["eventType", "النوع: concert أو festival أو sports"],
        ["imageUrl", "رابط الصورة"],
        ["imageAltAr", "وصف الصورة بالعربية"],
      ],
    },
    {
      label: "المكان والوقت",
      fields: [
        ["countryId", "رمز الدولة الداخلي"],
        ["countryNameAr", "الدولة بالعربية"],
        ["countryCode", "رمز الدولة ISO"],
        ["cityNameAr", "المدينة بالعربية"],
        ["venueName", "المكان"],
        ["startsAt", "البداية ISO"],
        ["endsAt", "النهاية ISO"],
        ["timezone", "المنطقة الزمنية"],
        ["datePrecision", "دقة التاريخ: day أو multiple_days"],
      ],
    },
    {
      label: "المصادر والنشر",
      fields: [
        ["officialSourceUrl", "الرابط الرسمي"],
        ["bookingUrl", "رابط الحجز"],
        ["sourceName", "اسم المصدر"],
        ["verificationStatus", "الحالة: confirmed أو announced_no_booking"],
        ["verifiedAt", "تاريخ التحقق ISO"],
        ["editorialRank", "أولوية العرض"],
      ],
    },
  ],
};

function getPath(source, path) {
  return path.split(".").reduce((value, key) => value?.[key], source);
}

function setPath(source, path, value) {
  const keys = path.split(".");
  const output = { ...source };
  let cursor = output;
  keys.forEach((key, index) => {
    if (index === keys.length - 1) cursor[key] = value;
    else {
      cursor[key] = { ...(cursor[key] || {}) };
      cursor = cursor[key];
    }
  });
  return output;
}

function statusColor(status) {
  return (
    {
      published: "success",
      draft: "warning",
      review: "info",
      archived: "default",
      suspended: "error",
      active: "success",
    }[status] || "default"
  );
}

const statusLabels = {
  published: "منشور",
  draft: "مسودة",
  review: "بانتظار المراجعة",
  archived: "مؤرشف",
  suspended: "معلّق",
  active: "نشط",
};

const actionPrompts = {
  duplicate: "هل تريد إنشاء نسخة قابلة للتعديل من هذا السجل؟",
  review: "هل تريد إرسال هذه المسودة للمراجعة؟",
  publish: "هل أنت متأكد من نشر هذا المحتوى للمسافرين؟",
  archive: "هل تريد أرشفة هذا السجل وإخفاءه من التطبيق؟",
};

function ResourcePage({ resource }) {
  const config = configs[resource];
  const auth = useAdminAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({});
  const [tab, setTab] = useState(0);
  const [saving, setSaving] = useState(false);
  const [fetching, setFetching] = useState(false);
  const [success, setSuccess] = useState("");
  const [tripDetail, setTripDetail] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    setError("");
    listAllResource(resource, { search, status })
      .then((payload) => setItems(unwrapItems(payload)))
      .catch((requestError) => setError(requestError.message))
      .finally(() => setLoading(false));
  }, [resource, search, status]);

  useEffect(() => {
    const timeout = setTimeout(load, 250);
    return () => clearTimeout(timeout);
  }, [load]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const requestedStatus = params.get("status");
    if (requestedStatus) setStatus(requestedStatus);
    if (params.get("new") === "1" && !config.readOnly && editing === null) {
      setEditing({});
      setForm({});
      setTab(0);
      navigate(location.pathname, { replace: true });
    }
  }, [config.readOnly, editing, location.pathname, location.search, navigate]);

  const openEditor = (item = null) => {
    const values = item?.draft || item?.effective || item || {};
    setEditing(item || {});
    setForm(values);
    setTab(0);
  };

  const closeEditor = () => {
    if (!saving) {
      setEditing(null);
      setForm({});
    }
  };

  const save = async () => {
    setSaving(true);
    setError("");
    try {
      const id = editing?.entityId || editing?.[config.id];
      const payload = id
        ? await updateResource(resource, id, {
            ...form,
            ...(editing?.version != null ? { expectedVersion: editing.version } : {}),
          })
        : await createResource(resource, form);
      setEditing(payload);
      setForm(payload.draft || payload.effective || form);
      load();
    } catch (requestError) {
      setError(
        requestError.status === 409
          ? "تم تعديل السجل من مستخدم آخر. أعد تحميل الصفحة قبل الحفظ."
          : requestError.message
      );
    } finally {
      setSaving(false);
    }
  };

  const action = async (item, name) => {
    const id = item.entityId || item[config.id];
    if (!id) return;
    if (!window.confirm(actionPrompts[name] || "هل تريد تنفيذ هذه العملية؟")) return;
    try {
      await resourceAction(resource, id, name, {
        environment: process.env.REACT_APP_ENVIRONMENT || "dev",
        confirm: true,
      });
      load();
    } catch (requestError) {
      setError(requestError.message);
    }
  };

  const userAction = async (item, name) => {
    try {
      await resourceAction("users", item.userId, name);
      load();
    } catch (requestError) {
      setError(requestError.message);
    }
  };

  const openTripSupport = async (item) => {
    const id = item.tripId || item.entityId;
    if (!id) return;
    setTripDetail({ trip: item, loading: true });
    try {
      const detail = await adminRequest(`/admin/trips/${id}`);
      setTripDetail(detail);
    } catch (requestError) {
      setTripDetail(null);
      setError(requestError.message);
    }
  };

  const fetchAutomation = async () => {
    if (!config.automation || fetching) return;
    if (!window.confirm(config.automation.confirmation)) return;
    setFetching(true);
    setError("");
    setSuccess("");
    try {
      await adminRequest(config.automation.path, { method: "POST", body: "{}" });
      setSuccess("تم بدء الجلب. ستظهر الأحداث الجديدة كمسودات بعد اكتمال الترجمة.");
      setTimeout(load, 2500);
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setFetching(false);
    }
  };

  const visibleItems = useMemo(() => items, [items]);

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox py={3}>
        <MDBox
          display="flex"
          flexWrap="wrap"
          justifyContent="space-between"
          alignItems="center"
          gap={2}
          mb={3}
        >
          <MDBox>
            <MDTypography variant="h4" fontWeight="bold">
              {config.title}
            </MDTypography>
            <MDTypography variant="button" color="text">
              {config.description}
            </MDTypography>
            {!loading && (
              <MDTypography variant="caption" color="text" display="block" mt={0.5}>
                {items.length} {items.length === 1 ? "سجل" : "سجلاً"}
              </MDTypography>
            )}
          </MDBox>
          <MDBox display="flex" gap={1} flexWrap="wrap">
            {config.automation && (
              <Button
                variant="outlined"
                disabled={fetching}
                startIcon={fetching ? <CircularProgress size={16} /> : <Icon>cloud_download</Icon>}
                onClick={fetchAutomation}
              >
                {fetching ? "جارٍ جلب الأحداث..." : "جلب أحداث من API"}
              </Button>
            )}
            {!config.readOnly && (
              <Button
                variant="contained"
                startIcon={<Icon>add</Icon>}
                onClick={() => openEditor()}
                sx={{ bgcolor: "#179B82", "&:hover": { bgcolor: "#12806B" } }}
              >
                {config.createLabel || "إضافة سجل"}
              </Button>
            )}
          </MDBox>
        </MDBox>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError("")}>
            {error}
          </Alert>
        )}
        {success && (
          <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess("")}>
            {success}
          </Alert>
        )}

        <MDBox bgColor="white" borderRadius="xl" shadow="sm" p={2.5}>
          <Grid container spacing={2} mb={2}>
            <Grid item xs={12} md={8}>
              <TextField
                fullWidth
                size="small"
                label={config.searchLabel || "بحث"}
                placeholder="اكتب للبحث"
                value={search}
                onChange={(event) => setSearch(event.target.value)}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <FormControl fullWidth size="small">
                <InputLabel>الحالة</InputLabel>
                <Select
                  value={status}
                  label="الحالة"
                  onChange={(event) => setStatus(event.target.value)}
                >
                  <MenuItem value="">الكل</MenuItem>
                  <MenuItem value="draft">مسودة</MenuItem>
                  <MenuItem value="review">للمراجعة</MenuItem>
                  <MenuItem value="published">منشور</MenuItem>
                  <MenuItem value="archived">مؤرشف</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>

          {loading ? (
            <MDBox py={8} textAlign="center">
              <CircularProgress color="success" />
            </MDBox>
          ) : visibleItems.length === 0 ? (
            <MDBox py={8} textAlign="center">
              <Icon sx={{ fontSize: 54, color: "#9CB0B8" }}>inventory_2</Icon>
              <MDTypography color="text">لا توجد نتائج مطابقة.</MDTypography>
            </MDBox>
          ) : (
            visibleItems.map((item) => {
              const id = item.entityId || item[config.id];
              return (
                <MDBox
                  key={id}
                  display="flex"
                  alignItems="center"
                  justifyContent="space-between"
                  gap={2}
                  py={1.75}
                  px={1}
                  borderBottom="1px solid #E1ECEF"
                >
                  <MDBox minWidth={0}>
                    <MDTypography variant="button" fontWeight="bold" display="block">
                      {config.primary(item)}
                    </MDTypography>
                    <MDTypography variant="caption" color="text">
                      {config.secondary(item)}
                    </MDTypography>
                  </MDBox>
                  <MDBox display="flex" alignItems="center" gap={0.5}>
                    <Chip
                      size="small"
                      label={statusLabels[item.status || "published"] || item.status || "منشور"}
                      color={statusColor(item.status || "published")}
                    />
                    {!config.readOnly && (
                      <>
                        <Button
                          size="small"
                          startIcon={<Icon>edit</Icon>}
                          onClick={() => openEditor(item)}
                        >
                          تعديل
                        </Button>
                        <Button size="small" onClick={() => action(item, "duplicate")}>
                          نسخ
                        </Button>
                        {item.status !== "published" && (
                          <Button size="small" onClick={() => action(item, "review")}>
                            طلب مراجعة
                          </Button>
                        )}
                        {auth.canPublish && (
                          <>
                            {item.status !== "published" && (
                              <Button
                                size="small"
                                color="success"
                                onClick={() => action(item, "publish")}
                              >
                                نشر
                              </Button>
                            )}
                            {item.status !== "archived" && (
                              <Button
                                size="small"
                                color="error"
                                onClick={() => action(item, "archive")}
                              >
                                أرشفة
                              </Button>
                            )}
                          </>
                        )}
                      </>
                    )}
                    {resource === "users" && auth.canPublish && (
                      <Button
                        size="small"
                        color={item.status === "suspended" ? "success" : "error"}
                        onClick={() =>
                          userAction(item, item.status === "suspended" ? "reactivate" : "suspend")
                        }
                      >
                        {item.status === "suspended" ? "إعادة تفعيل" : "تعطيل"}
                      </Button>
                    )}
                    {resource === "trips" && (
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() => openTripSupport(item)}
                        sx={{
                          color: "#087A67",
                          borderColor: "#18A88B",
                          fontWeight: 700,
                          "&:hover": {
                            color: "#055F51",
                            borderColor: "#087A67",
                            backgroundColor: "rgba(24, 168, 139, 0.08)",
                          },
                        }}
                      >
                        فتح رحلة
                      </Button>
                    )}
                  </MDBox>
                </MDBox>
              );
            })
          )}
        </MDBox>
      </MDBox>

      <EditorDialog
        open={editing !== null}
        resource={resource}
        form={form}
        setForm={setForm}
        tab={tab}
        setTab={setTab}
        saving={saving}
        error={error}
        onClose={closeEditor}
        onSave={save}
        entityId={editing?.entityId || editing?.[config.id]}
      />
      <TripSupportDialog
        detail={tripDetail}
        canManage={auth.canPublish}
        onClose={() => setTripDetail(null)}
        onUpdate={setTripDetail}
      />
    </DashboardLayout>
  );
}

function TripSupportDialog({ detail, canManage, onClose, onUpdate }) {
  const [note, setNote] = useState("");
  const [savingNote, setSavingNote] = useState(false);
  const trip = detail?.trip;

  const addNote = async () => {
    if (!trip?.tripId || !note.trim() || savingNote) return;
    setSavingNote(true);
    try {
      const saved = await adminRequest(`/admin/trips/${trip.tripId}/support-notes`, {
        method: "POST",
        body: JSON.stringify({ body: note.trim() }),
      });
      onUpdate((current) => ({
        ...current,
        supportNotes: [...(current.supportNotes || []), saved],
      }));
      setNote("");
    } finally {
      setSavingNote(false);
    }
  };

  const updateStatus = async (status) => {
    if (!trip?.tripId) return;
    const updated = await adminRequest(`/admin/trips/${trip.tripId}/status`, {
      method: "PATCH",
      body: JSON.stringify({ status }),
    });
    onUpdate((current) => ({ ...current, trip: updated }));
  };

  const segment = trip?.segments?.[0] || {};
  const completedTasks = (detail?.tasks || []).filter((item) => item.isDone).length;

  return (
    <Dialog open={Boolean(detail)} onClose={onClose} fullWidth maxWidth="md" dir="rtl">
      <DialogTitle>{trip?.title || "تفاصيل الرحلة"}</DialogTitle>
      <DialogContent dividers>
        {detail?.loading ? (
          <MDBox py={6} textAlign="center">
            <CircularProgress color="success" />
          </MDBox>
        ) : trip ? (
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <MDTypography variant="button" color="text">
                الوجهة
              </MDTypography>
              <MDTypography fontWeight="bold">
                {[segment.country, segment.city].filter(Boolean).join(" · ") || "—"}
              </MDTypography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <MDTypography variant="button" color="text">
                التواريخ
              </MDTypography>
              <MDTypography fontWeight="bold">
                {trip.startDate || "—"} إلى {trip.endDate || "—"}
              </MDTypography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <MDTypography variant="button" color="text">
                الميزانية
              </MDTypography>
              <MDTypography fontWeight="bold">
                {trip.budgetAmount || "—"} {trip.currency || ""}
              </MDTypography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <MDTypography variant="button" color="text">
                التحضيرات
              </MDTypography>
              <MDTypography fontWeight="bold">
                {completedTasks} من {(detail.tasks || []).length} مكتملة
              </MDTypography>
            </Grid>
            <Grid item xs={12}>
              <MDBox bgColor="grey-100" borderRadius="lg" p={2}>
                <MDTypography variant="button" fontWeight="bold" display="block">
                  ملخص الرحلة
                </MDTypography>
                <MDTypography variant="caption" color="text">
                  {(detail.activities || []).length} نشاطات · {(detail.expenses || []).length}{" "}
                  مصروفات مسجلة
                </MDTypography>
              </MDBox>
            </Grid>
            {canManage && (
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel>حالة المتابعة</InputLabel>
                  <Select
                    value={trip.status || "planning"}
                    label="حالة المتابعة"
                    onChange={(event) => updateStatus(event.target.value)}
                  >
                    <MenuItem value="planning">قيد التخطيط</MenuItem>
                    <MenuItem value="active">رحلة جارية</MenuItem>
                    <MenuItem value="support_review">تحتاج متابعة</MenuItem>
                    <MenuItem value="completed">مكتملة</MenuItem>
                    <MenuItem value="cancelled">ملغاة</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            )}
            <Grid item xs={12}>
              <MDTypography variant="h6" fontWeight="bold" mb={1}>
                ملاحظات الدعم الداخلية
              </MDTypography>
              {(detail.supportNotes || []).map((item) => (
                <MDBox
                  key={item.noteId}
                  border="1px solid #E1ECEF"
                  borderRadius="lg"
                  p={1.5}
                  mb={1}
                >
                  <MDTypography variant="button">{item.body}</MDTypography>
                  <MDTypography variant="caption" color="text" display="block">
                    {item.createdBy?.name || "فريق الإدارة"} · {item.createdAt || ""}
                  </MDTypography>
                </MDBox>
              ))}
              <TextField
                fullWidth
                multiline
                minRows={2}
                label="أضف ملاحظة للدعم (لا تظهر للمسافر)"
                value={note}
                onChange={(event) => setNote(event.target.value)}
                sx={{ mt: 1 }}
              />
              <Button
                sx={{ mt: 1 }}
                variant="outlined"
                disabled={!note.trim() || savingNote}
                onClick={addNote}
              >
                {savingNote ? "جارٍ الحفظ..." : "حفظ ملاحظة الدعم"}
              </Button>
            </Grid>
          </Grid>
        ) : null}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>إغلاق</Button>
      </DialogActions>
    </Dialog>
  );
}

function EditorDialog({
  open,
  resource,
  form,
  setForm,
  tab,
  setTab,
  saving,
  error,
  onClose,
  onSave,
  entityId,
}) {
  const groups = fieldGroups[resource] || [];
  const active = groups[tab] || groups[0];
  const totalSteps = groups.length;
  const [uploading, setUploading] = useState(false);
  const [uploadInfo, setUploadInfo] = useState("");

  useEffect(() => {
    setUploadInfo("");
  }, [entityId, open]);

  const change = (path, value, mode) => {
    const normalized = mode === "lines" ? value.split("\n").filter(Boolean) : value;
    setForm((current) => setPath(current, path, normalized));
  };

  const upload = async (event) => {
    const file = event.target.files?.[0];
    if (!file || !entityId) return;
    setUploading(true);
    setUploadInfo("");
    try {
      const media = await uploadMedia({ file, entityType: resource, entityId });
      setForm((current) => ({
        ...current,
        heroImage: current.heroImage || media.publicUrl,
        media: [
          ...(current.media || []),
          {
            url: media.publicUrl,
            alt: current.displayName || current.nameAr || "",
            isCover: false,
          },
        ],
      }));
      setUploadInfo(media.optimization?.summary || "تم رفع الصورة بنجاح.");
    } finally {
      setUploading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="lg" dir="rtl">
      <DialogTitle>
        {entityId ? "تحرير السجل" : "إضافة سجل جديد"}
        {totalSteps > 1 && (
          <MDTypography variant="caption" color="text" display="block" mt={0.5}>
            الخطوة {tab + 1} من {totalSteps}: {active?.label}
          </MDTypography>
        )}
      </DialogTitle>
      {groups.length > 1 && (
        <Tabs
          value={tab}
          onChange={(_, value) => setTab(value)}
          variant="scrollable"
          scrollButtons="auto"
          sx={{ px: 3 }}
        >
          {groups.map((group) => (
            <Tab key={group.label} label={group.label} />
          ))}
        </Tabs>
      )}
      <DialogContent dividers>
        {error && <Alert severity="error">{error}</Alert>}
        {!error && (
          <Alert severity="info" sx={{ mb: 2 }}>
            الحقول التي تحمل نجمة مطلوبة للنشر. يمكنك الحفظ كمسودة وإكمال بقية المعلومات لاحقاً.
          </Alert>
        )}
        {(resource === "destinations" || resource === "places") && !entityId && (
          <Alert severity="info" sx={{ mb: 2 }}>
            احفظ المعلومات الأساسية أولاً، ثم يمكنك رفع الصور وربطها بهذا السجل.
          </Alert>
        )}
        <Grid container spacing={2} mt={0}>
          {(active?.fields || []).map(([path, label, mode]) => {
            const value = getPath(form, path);
            const text = mode === "lines" && Array.isArray(value) ? value.join("\n") : value || "";
            const required = [
              "country",
              "area",
              "displayName",
              "category",
              "travelStyles",
              "heroImage",
              "tagline",
              "destinationId",
              "nameAr",
              "placeType",
              "title",
              "message",
              "scope",
              "titleAr",
              "summaryAr",
              "descriptionAr",
              "countryId",
              "countryNameAr",
              "countryCode",
              "eventType",
              "startsAt",
              "endsAt",
              "timezone",
              "datePrecision",
              "imageUrl",
              "imageAltAr",
              "officialSourceUrl",
              "verificationStatus",
              "verifiedAt",
              "sourceName",
            ].includes(path);
            return (
              <Grid item xs={12} md={mode === "multiline" || mode === "lines" ? 12 : 6} key={path}>
                <TextField
                  fullWidth
                  label={label}
                  value={text}
                  required={required}
                  multiline={mode === "multiline" || mode === "lines"}
                  minRows={mode === "multiline" || mode === "lines" ? 3 : undefined}
                  helperText={mode === "lines" ? "اكتب كل عنصر في سطر مستقل." : undefined}
                  onChange={(event) => change(path, event.target.value, mode)}
                />
              </Grid>
            );
          })}
          {(resource === "destinations" || resource === "places") && entityId && (
            <Grid item xs={12}>
              <Button component="label" variant="outlined" disabled={uploading}>
                {uploading ? "جارٍ رفع الصورة..." : "رفع صورة"}
                <input
                  hidden
                  type="file"
                  accept="image/jpeg,image/png,image/webp"
                  onChange={upload}
                />
              </Button>
              {uploadInfo && (
                <MDTypography variant="caption" color="success" display="block" mt={1}>
                  {uploadInfo}
                </MDTypography>
              )}
            </Grid>
          )}
          {(form.media || []).map((media, index) => (
            <Grid item xs={12} key={`${media.url}-${index}`}>
              <MDBox
                display="flex"
                alignItems="center"
                gap={1}
                p={1.5}
                border="1px solid #E1ECEF"
                borderRadius="lg"
              >
                <MDBox
                  component="img"
                  src={media.url}
                  alt={media.alt || ""}
                  width="5rem"
                  height="3.5rem"
                  borderRadius="md"
                  sx={{ objectFit: "cover" }}
                />
                <TextField
                  fullWidth
                  size="small"
                  label="النص البديل"
                  value={media.alt || ""}
                  onChange={(event) =>
                    setForm((current) => ({
                      ...current,
                      media: current.media.map((item, itemIndex) =>
                        itemIndex === index ? { ...item, alt: event.target.value } : item
                      ),
                    }))
                  }
                />
                <Tooltip title="صورة الغلاف">
                  <IconButton
                    color={media.isCover ? "success" : "default"}
                    onClick={() =>
                      setForm((current) => ({
                        ...current,
                        heroImage: media.url,
                        media: current.media.map((item, itemIndex) => ({
                          ...item,
                          isCover: itemIndex === index,
                        })),
                      }))
                    }
                  >
                    <Icon>{media.isCover ? "star" : "star_border"}</Icon>
                  </IconButton>
                </Tooltip>
                <IconButton
                  disabled={index === 0}
                  onClick={() =>
                    setForm((current) => {
                      const mediaItems = [...current.media];
                      [mediaItems[index - 1], mediaItems[index]] = [
                        mediaItems[index],
                        mediaItems[index - 1],
                      ];
                      return { ...current, media: mediaItems };
                    })
                  }
                >
                  <Icon>arrow_upward</Icon>
                </IconButton>
                <IconButton
                  disabled={index === form.media.length - 1}
                  onClick={() =>
                    setForm((current) => {
                      const mediaItems = [...current.media];
                      [mediaItems[index + 1], mediaItems[index]] = [
                        mediaItems[index],
                        mediaItems[index + 1],
                      ];
                      return { ...current, media: mediaItems };
                    })
                  }
                >
                  <Icon>arrow_downward</Icon>
                </IconButton>
              </MDBox>
            </Grid>
          ))}
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>إلغاء</Button>
        {totalSteps > 1 && tab > 0 && (
          <Button onClick={() => setTab((current) => current - 1)}>السابق</Button>
        )}
        {totalSteps > 1 && tab < totalSteps - 1 && (
          <Button onClick={() => setTab((current) => current + 1)}>التالي</Button>
        )}
        <Button
          variant="contained"
          onClick={onSave}
          disabled={saving}
          sx={{ bgcolor: "#179B82", "&:hover": { bgcolor: "#12806B" } }}
        >
          {saving ? "جارٍ الحفظ..." : "حفظ كمسودة"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

ResourcePage.propTypes = { resource: PropTypes.string.isRequired };
EditorDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  resource: PropTypes.string.isRequired,
  form: PropTypes.object.isRequired,
  setForm: PropTypes.func.isRequired,
  tab: PropTypes.number.isRequired,
  setTab: PropTypes.func.isRequired,
  saving: PropTypes.bool.isRequired,
  error: PropTypes.string.isRequired,
  onClose: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  entityId: PropTypes.string,
};
EditorDialog.defaultProps = { entityId: null };
TripSupportDialog.propTypes = {
  detail: PropTypes.object,
  canManage: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onUpdate: PropTypes.func.isRequired,
};
TripSupportDialog.defaultProps = { detail: null };

export default ResourcePage;
